import { useState } from "react";
import { useShopGridUpdate } from "queries/shop_grid";
import { Api } from "queries/types";
import { State } from "./types";
import { flattenDeep, pickBy, uniq, uniqBy } from "lodash";
import buildGridState from "./buildGridState";
import gridStateToPayload from "./gridStateToPayload";

const editCells = (cells: State.Cell[], lines: State.Cell[][], attributes: Partial<Omit<State.Cell, "id">>) =>
  lines.map((line) =>
    line.map((cell) => {
      if (!cells.some((gridCell) => gridCell === cell)) return cell;

      const updatedCell = { ...cell, ...pickBy(attributes, (value) => value !== undefined) };
      updatedCell.sections = updatedCell.sections.slice(0, updatedCell.shelvesCount);

      return updatedCell;
    })
  );

interface ControllerOptions {
  families?: Api.Family[];
  gridProposal?: Api.ShopGrid;
  isFurniture?: Boolean;
}

const useShopGridController = (activeGrid: Api.ShopGrid, { families = [], gridProposal, isFurniture = false }: ControllerOptions = {}) => {
  const [grid, setGrid] = useState<State.Grid>(() => buildGridState(activeGrid, gridProposal, families));
  const [selectedCells, setSelectedCells] = useState<State.Cell[]>([]);
  const [selectedViewCell, setSelectedViewCell] = useState<State.Cell>();
  const [viewMode, setViewMode] = useState<Boolean>(false);

  const updateMutation = useShopGridUpdate();

  const toggleCell = (cell: State.Cell) => {
    if (viewMode) {
      setSelectedViewCell(cell)
    } else {
      const isSelected = selectedCells.some((selectedCell) => selectedCell === cell);

      if (isSelected) setSelectedCells((selectedCells) => selectedCells.filter((selectedCell) => selectedCell !== cell));
      else setSelectedCells((cells) => uniq([cell, ...cells]));
    }
  };

  const closeViewCell = () => {
    setSelectedViewCell(undefined);
  };

  const switchToViewMode = (view: boolean) => {
    setViewMode(view);
    unselectAll();
  };

  const unselectAll = () => setSelectedCells([]);

  const configureCells = (attributes: Partial<Omit<State.Cell, "id">>) => {

    // Reset Id of cell when we update recordable furniture to one that isn't
    if (!attributes.furniture?.isRecordable) {
      selectedCells.forEach(cell => {if (cell.furniture?.isRecordable) cell.sections = []; });
    }

    setGrid((grid) => {
      const updatedGrid = { ...grid, lines: editCells(selectedCells, grid.lines, attributes) };
      save(updatedGrid);
      return updatedGrid;
    });
  };

  const save = (grid: State.Grid) =>
    updateMutation
      .mutateAsync(gridStateToPayload(grid, (gridProposal || activeGrid).id))
      .then((response) => {
        if (response.data && !isFurniture) setGrid(buildGridState(activeGrid, response.data, families));
        else if (response.data) setGrid(buildGridState(response.data, gridProposal, families));
      });

  const gridFurnitures = uniqBy(
    flattenDeep(grid.lines.map((line) => line.map((cell) => cell.furniture))).filter(Boolean),
    "id"
  ) as Api.ShopFurniture[];

  const gridFamilies = uniqBy(
    flattenDeep(
      grid.lines.map((line) =>
        line.map((cell) => cell.sections.map((section) => section.inventories.map((inventory) => inventory.family)))
      )
    ).filter(Boolean),
    "id"
  ) as Api.Family[];

  const isSaving = updateMutation.isLoading;

  return {
    grid,
    configureCells,
    isSaving,
    toggleCell,
    selectedCells,
    unselectAll,
    gridFurnitures,
    gridFamilies,
    closeViewCell,
    switchToViewMode,
    selectedViewCell,
    viewMode,
  };
};

export default useShopGridController;
